import React from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import {
  PermissionLevel,
  PORTAL_ROLES,
  ROLE_PERMISSIONS,
} from '../config/rolePermissions'

const Container = styled.section`
  margin-bottom: 2rem;
`

const Intro = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 1.25rem;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`

const IntroCopy = styled.div`
  h4 {
    color: ${theme.color.text.base};
    font-size: 1.2rem;
    margin: 0 0 0.4rem;
  }

  p {
    color: ${theme.color.text.grey};
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0;
    max-width: 680px;
  }
`

const CurrentBadge = styled.span`
  flex: 0 0 auto;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(76, 175, 80, 0.14);
  border: 1px solid rgba(76, 175, 80, 0.35);
  color: #23692b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const DriftNotice = styled.div`
  background: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.25);
  border-left: 4px solid #ffc107;
  border-radius: 6px;
  color: ${theme.color.text.base};
  font-size: 0.85rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
`

const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

const RoleCard = styled.article`
  background: ${theme.color.background.dark};
  border: 1px solid #444;
  border-radius: 8px;
  padding: 1rem;

  h5 {
    color: ${theme.color.text.base};
    font-size: 1rem;
    margin: 0 0 0.25rem;
  }

  p {
    color: ${theme.color.text.grey};
    font-size: 0.8rem;
    line-height: 1.45;
    margin: 0.65rem 0 0;
  }
`

const Audience = styled.div`
  color: #825c00;
  font-size: 0.75rem;
  font-weight: 600;
`

const GroupName = styled.code`
  display: block;
  color: #42556d;
  font-size: 0.7rem;
  margin-top: 0.65rem;
  overflow-wrap: anywhere;
`

const MatrixScroller = styled.div`
  border: 1px solid #444;
  border-radius: 8px;
  overflow-x: auto;
`

const Matrix = styled.table`
  border-collapse: collapse;
  min-width: 820px;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid #3b3b3b;
    padding: 0.8rem;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #252525;
    color: #fff;
    font-size: 0.78rem;
    letter-spacing: 0.04em;
    position: sticky;
    top: 0;
    text-transform: uppercase;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: rgba(255, 255, 255, 0.025);
  }
`

const AreaCell = styled.td`
  background: rgba(255, 255, 255, 0.02);
  color: ${theme.color.text.base};
  font-size: 0.86rem;
  font-weight: 650;
  width: 170px;
`

const Category = styled.span`
  color: ${theme.color.text.grey};
  display: block;
  font-size: 0.67rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  margin-top: 0.3rem;
  text-transform: uppercase;
`

const PermissionCell = styled.td`
  color: ${theme.color.text.grey};
  font-size: 0.76rem;
  line-height: 1.4;
  width: calc((100% - 170px) / 3);
`

const LEVEL_STYLES: Record<PermissionLevel, { label: string; color: string; background: string }> = {
  full: { label: 'Full', color: '#23692b', background: 'rgba(76, 175, 80, 0.14)' },
  limited: { label: 'Limited', color: '#825c00', background: 'rgba(255, 193, 7, 0.12)' },
  'read-only': { label: 'Read only', color: '#17649e', background: 'rgba(33, 150, 243, 0.12)' },
  none: { label: 'No access', color: '#555', background: 'rgba(255, 255, 255, 0.06)' },
}

const LevelBadge = styled.span<{ level: PermissionLevel }>`
  background: ${(props) => LEVEL_STYLES[props.level].background};
  border-radius: 999px;
  color: ${(props) => LEVEL_STYLES[props.level].color};
  display: inline-block;
  font-size: 0.67rem;
  font-weight: 700;
  margin-bottom: 0.45rem;
  padding: 0.25rem 0.55rem;
  text-transform: uppercase;
`

const AdjustmentNote = styled.p`
  color: ${theme.color.text.grey};
  font-size: 0.8rem;
  line-height: 1.5;
  margin: 0.9rem 0 0;
`

const RolePermissions: React.FC = () => (
  <Container aria-labelledby="role-permissions-heading">
    <Intro>
      <IntroCopy>
        <h4 id="role-permissions-heading">Role permissions</h4>
        <p>
          This matrix reflects the access enforced by the Admin Tool today. Assigning a different
          Okta role below changes a person's effective permissions after their next sign-in.
        </p>
      </IntroCopy>
      <CurrentBadge>Current access</CurrentBadge>
    </Intro>

    <DriftNotice role="note">
      Roles follow the approved permissions matrix. Customer actions are limited by role;
      gift-card limits, merge gates and challenge completion overrides retain their separate controls.
    </DriftNotice>

    <RoleGrid>
      {PORTAL_ROLES.map((role) => (
        <RoleCard key={role.id}>
          <h5>{role.label}</h5>
          <Audience>{role.audience}</Audience>
          <p>{role.summary}</p>
          <GroupName>{role.oktaGroup}</GroupName>
        </RoleCard>
      ))}
    </RoleGrid>

    <MatrixScroller tabIndex={0} aria-label="Current role permission matrix">
      <Matrix>
        <thead>
          <tr>
            <th scope="col">Area</th>
            {PORTAL_ROLES.map((role) => <th scope="col" key={role.id}>{role.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {ROLE_PERMISSIONS.map((permission) => (
            <tr key={permission.id}>
              <AreaCell>
                {permission.area}
                <Category>{permission.category}</Category>
              </AreaCell>
              {PORTAL_ROLES.map((role) => {
                const access = permission.access[role.id]
                return (
                  <PermissionCell key={role.id}>
                    <LevelBadge level={access.level}>{LEVEL_STYLES[access.level].label}</LevelBadge>
                    <div>{access.detail}</div>
                  </PermissionCell>
                )
              })}
            </tr>
          ))}
        </tbody>
      </Matrix>
    </MatrixScroller>

    <AdjustmentNote>
      To adjust a person's access, search for them below and add or remove one of the three Admin
      Tool role groups. Super Admin permissions remain protected by server-side checks.
    </AdjustmentNote>
  </Container>
)

export default RolePermissions
